import { useState, useEffect, useMemo } from 'react';
import { Select, Button, Alert, Spin, Typography, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined
} from '@ant-design/icons';
import { sessionsService, type Session } from '../../api/sessionsService';
import { 
  sessionWeatherService, 
  type StationWeatherData, 
  type SessionWeatherData,
  type SessionWeatherResponse,
  type MetricAggregate,
  getMetricValue 
} from '../../api/sessionWeatherService';
import eventsService, { type Station } from '../../api/eventsService';
import { getEventSource } from '../../utils/eventSource';
import { parseDateOnly } from '../../utils/dateUtils';
import { useEvent } from '../../hooks/useEvent';
import { useObservationFields } from '../../hooks/useObservationFields';
import { getFieldIcon } from '../Forecast/icons/fieldIconMap';
import './SessionComparison.css';

const { Text } = Typography;

export interface SessionComparisonProps {
  /** When set, compare sessions for this circuit without a selected event */
  circuitId?: string;
  stations?: Station[];
  circuitTimeZone?: string;
  demo?: boolean;
  legacy?: boolean;
}

// Conversion constant for wind speed/gust (m/s to km/h)
const MS_TO_KMH = 3.6;

// Fields that require m/s to km/h conversion
const WIND_SPEED_FIELDS = ['windSpeed', 'windGust'];

interface WeatherMetricConfig {
  key: string;
  label: string;
  metricName: string;    // Maps to metric.name in the metrics array (observation field name)
  unit: string;
  hasMinMax: boolean;
  formatter?: (value: number) => string;
  convertToKmh?: boolean; // If true, convert from m/s to km/h for display (backend returns raw m/s)
}

interface TableDataRow {
  key: string;
  config: WeatherMetricConfig;
  metricName: string;
  leftValue: number | string | null;
  rightValue: number | string | null;
  delta: number | null;
  unit: string;
  formatter?: (value: number) => string;
}

export const SessionComparison = ({
  circuitId: circuitIdProp,
  stations: stationsProp,
  circuitTimeZone: circuitTimeZoneProp,
  demo: demoProp,
  legacy: legacyProp,
}: SessionComparisonProps = {}) => {
  const { selectedEvent } = useEvent();
  const circuitId = circuitIdProp ?? selectedEvent?.circuit_id;
  const effectiveStations = stationsProp ?? selectedEvent?.stations ?? [];
  const effectiveCircuitTimeZone = circuitTimeZoneProp ?? selectedEvent?.circuit?.time_zone;
  const effectiveDemo = demoProp ?? selectedEvent?.demo;
  const effectiveLegacy = legacyProp ?? selectedEvent?.legacy;
  const { getSessionCompareFields, getDisplayUnit } = useObservationFields();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Build weather metrics dynamically from observation_fields table
  // Fields with include_in_session_compare = true will appear here
  const metricsWithUnits: WeatherMetricConfig[] = useMemo(() => {
    const fields = getSessionCompareFields(effectiveDemo);
    
    return fields.map(field => {
      const isWindField = WIND_SPEED_FIELDS.includes(field.name);
      // For wind fields, display in km/h even though stored in m/s
      const displayUnit = isWindField ? 'km/h' : (getDisplayUnit(field.name) || field.unit || '');
      
      // Determine formatter based on unit/field type
      let formatter: ((val: number) => string) | undefined;
      if (field.unit === '%' || displayUnit === '%') {
        formatter = (val) => val.toFixed(1);
      } else if (field.name.toLowerCase().includes('pressure')) {
        formatter = (val) => val.toFixed(0);
      } else if (field.name.toLowerCase().includes('rain')) {
        formatter = (val) => val.toFixed(2);
      } else {
        formatter = (val) => val.toFixed(1);
      }
      
      return {
        key: field.name,
        label: field.display_name,
        metricName: field.name,  // Use observation field name directly
        unit: displayUnit,
        hasMinMax: !field.name.toLowerCase().includes('direction'),
        formatter,
        convertToKmh: isWindField,
      };
    });
  }, [getSessionCompareFields, getDisplayUnit, effectiveDemo]);

  // Left side
  const [leftSessionId, setLeftSessionId] = useState<string | null>(null);
  const [leftStation, setLeftStation] = useState<string>('all');
  const [leftWeatherData, setLeftWeatherData] = useState<StationWeatherData | null>(null);
  const [loadingLeftWeather, setLoadingLeftWeather] = useState(false);

  // Right side
  const [rightSessionId, setRightSessionId] = useState<string | null>(null);
  const [rightStation, setRightStation] = useState<string>('all');
  const [rightWeatherData, setRightWeatherData] = useState<StationWeatherData | null>(null);
  const [loadingRightWeather, setLoadingRightWeather] = useState(false);

  // Expanded rows
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // Convert wind direction to arrow
  const getWindDirectionArrow = (direction: string | null | undefined): string => {
    if (!direction) {
      return 'N/A';
    }
    
    const directionMap: Record<string, string> = {
      'n': '↑',
      'nne': '↑↗',
      'ne': '↗',
      'ene': '→↗',
      'e': '→',
      'ese': '→↘',
      'se': '↘',
      'sse': '↓↘',
      's': '↓',
      'ssw': '↓↙',
      'sw': '↙',
      'so': '↙', // French: Sud-Ouest (Southwest)
      'wsw': '←↙',
      'w': '←',
      'wnw': '←↖',
      'nw': '↖',
      'no': '↖', // French: Nord-Ouest (Northwest)
      'nnw': '↑↖',
      'o': '→', // French: Ouest (West) / Spanish: Oeste (East)
      'oso': '→↘',
      'sso': '↓↘',
      'ono': '→↗',
      'nno': '↑↗',
    };
    
    return directionMap[direction.toLowerCase()] || direction.toUpperCase();
  };

  // Load sessions when circuit changes - load all years and all session types for this circuit
  useEffect(() => {
    if (circuitId) {
      setLoadingSessions(true);
      
      // Fetch sessions for this circuit (now embedded in events)
      sessionsService
        .getSessions({ 
          circuit_id: circuitId
        })
        .then((data) => {
          // Sort by year descending, then by start_date/start_time
          const sortedSessions = data.sort((a, b) => {
            if (a.season_year !== b.season_year) {
              return b.season_year - a.season_year;
            }
            // If same year, sort by start_date and start_time
            const dateCompare = b.start_date.localeCompare(a.start_date);
            if (dateCompare !== 0) return dateCompare;
            return b.start_time.localeCompare(a.start_time);
          });
          setSessions(sortedSessions);
          
          // Auto-select: latest RACE session as left, previous year's RACE as right
          const raceSessions = sortedSessions.filter(s => s.session_name === 'RACE');
          if (raceSessions.length > 0) {
            const latestRace = raceSessions[0];
            const latestKey = `${latestRace.season_year}|${latestRace.circuit_id}|${latestRace.start_date}|${latestRace.session_name}`;
            setLeftSessionId(latestKey);
            
            if (raceSessions.length > 1) {
              const previousRace = raceSessions[1];
              const previousKey = `${previousRace.season_year}|${previousRace.circuit_id}|${previousRace.start_date}|${previousRace.session_name}`;
              setRightSessionId(previousKey);
            }
          }
        })
        .catch((err: Error) => console.error('Failed to load sessions:', err))
        .finally(() => setLoadingSessions(false));
    }
  }, [circuitId, effectiveLegacy]);

  // Load left weather data
  useEffect(() => {
    if (leftSessionId) {
      const [season_year, circuit_id, start_date, session_name] = leftSessionId.split('|');
      setLoadingLeftWeather(true);
      
      // Fetch the event to determine its source
      eventsService
        .getEvent(Number(season_year), circuit_id, start_date)
        .then((eventResponse) => {
          if (!eventResponse.success || !eventResponse.data) {
            throw new Error('Event not found');
          }
          
          const event = eventResponse.data;
          const source = getEventSource(event);
          
          return sessionWeatherService.getSessionWeatherByEvent(
            Number(season_year), 
            circuit_id, 
            start_date, 
            source
          );
        })
        .then((response: SessionWeatherResponse) => {
          const sessionsArray = (response as SessionWeatherResponse).data || (response as unknown as SessionWeatherData[]);
          
          if (Array.isArray(sessionsArray)) {
            const sessionData = sessionsArray.find((s: SessionWeatherData) => s.session_name === session_name);
            if (sessionData) {
              const aggregatedData = findStationData(sessionData.stations, leftStation);
              setLeftWeatherData(aggregatedData);
            } else {
              setLeftWeatherData(null);
            }
          } else {
            setLeftWeatherData(null);
          }
        })
        .catch((err: Error) => {
          // Don't log as error if it's just missing data (handled gracefully with null state)
          console.log('[LEFT] No weather data available:', err.message);
          setLeftWeatherData(null);
        })
        .finally(() => setLoadingLeftWeather(false));
    }
  }, [leftSessionId, leftStation]);

  // Load right weather data
  useEffect(() => {
    if (rightSessionId) {
      const [season_year, circuit_id, start_date, session_name] = rightSessionId.split('|');
      setLoadingRightWeather(true);
      
      // Fetch the event to determine its source
      eventsService
        .getEvent(Number(season_year), circuit_id, start_date)
        .then((eventResponse) => {
          if (!eventResponse.success || !eventResponse.data) {
            throw new Error('Event not found');
          }
          
          const event = eventResponse.data;
          const source = getEventSource(event);
          
          return sessionWeatherService.getSessionWeatherByEvent(
            Number(season_year), 
            circuit_id, 
            start_date, 
            source
          );
        })
        .then((response: SessionWeatherResponse) => {
          const sessionsArray = (response as SessionWeatherResponse).data || (response as unknown as SessionWeatherData[]);
          
          if (Array.isArray(sessionsArray)) {
            const sessionData = sessionsArray.find((s: SessionWeatherData) => s.session_name === session_name);
            if (sessionData) {
              const aggregatedData = findStationData(sessionData.stations, rightStation);
              setRightWeatherData(aggregatedData);
            } else {
              setRightWeatherData(null);
            }
          } else {
            setRightWeatherData(null);
          }
        })
        .catch((err: Error) => {
          // Don't log as error if it's just missing data (handled gracefully with null state)
          console.log('[RIGHT] No weather data available:', err.message);
          setRightWeatherData(null);
        })
        .finally(() => setLoadingRightWeather(false));
    }
  }, [rightSessionId, rightStation]);

  // Helper function to find station data by reference (simplified with new structure)
  const findStationData = (stations: StationWeatherData[], stationRef: string): StationWeatherData | null => {
    if (stations.length === 0) return null;
    
    // With new structure, just find by reference - 'all' is pre-computed
    const station = stations.find(s => s.station_reference === stationRef);
    if (station) return station;
    
    // If stationRef is 'all' and no 'all' station exists, try to aggregate manually
    if (stationRef === 'all' && stations.length > 0) {
      // Create aggregated metrics from all stations
      const metricNames = new Set<string>();
      stations.forEach(s => s.metrics.forEach(m => metricNames.add(m.name)));
      
      const aggregatedMetrics: MetricAggregate[] = Array.from(metricNames).map(name => {
        const values = stations
          .map(s => s.metrics.find(m => m.name === name))
          .filter((m): m is MetricAggregate => m !== undefined);
        
        return {
          name,
          min: Math.min(...values.map(v => v.min).filter((v): v is number => v !== null)),
          max: Math.max(...values.map(v => v.max).filter((v): v is number => v !== null)),
          avg: values.reduce((sum, v) => sum + (v.avg || 0), 0) / values.filter(v => v.avg !== null).length || null,
          sum: values.reduce((sum, v) => sum + (v.sum || 0), 0),
          count: values.reduce((sum, v) => sum + v.count, 0),
        };
      });

      // Aggregate wind direction by pooling counts across all stations and finding the mode
      let aggregatedWindDirection: StationWeatherData['main_wind_direction'] = null;
      const stationsWithWind = stations.filter(s => s.main_wind_direction);
      if (stationsWithWind.length > 0) {
        const directionCounts = new Map<string, number>();
        let totalCount = 0;
        for (const s of stationsWithWind) {
          const wd = s.main_wind_direction!;
          const stationTotal = wd.count || 0;
          const modeCount = Math.round((wd.frequency / 100) * stationTotal);
          directionCounts.set(wd.direction, (directionCounts.get(wd.direction) || 0) + modeCount);
          totalCount += stationTotal;
        }
        let bestDirection = '';
        let bestCount = 0;
        for (const [dir, count] of directionCounts) {
          if (count > bestCount) {
            bestDirection = dir;
            bestCount = count;
          }
        }
        if (bestDirection && totalCount > 0) {
          aggregatedWindDirection = {
            direction: bestDirection,
            frequency: Math.round((bestCount / totalCount) * 10000) / 100,
            count: totalCount,
          };
        }
      }
      
      return {
        station_reference: 'all',
        metrics: aggregatedMetrics,
        main_wind_direction: aggregatedWindDirection,
        nb_stations: stations.length,
      };
    }
    
    return null;
  };

  const toggleRow = (key: string) => {
    setExpandedRowKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const formatSessionOption = (session: Session) => {
    const date = parseDateOnly(session.start_date);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${month}-${day}`;
    
    const circuitTimeZone = effectiveCircuitTimeZone;
    
    let startTime = '--:--';
    let endTime = '--:--';
    let sessionDate = '';
    
    if (session.start_time) {
      startTime = sessionsService.formatSessionTime(session.start_time, circuitTimeZone);
      const startDate = new Date(session.start_time);
      const localDate = new Date(startDate.toLocaleString('en-US', { timeZone: circuitTimeZone }));
      sessionDate = `${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
    }
    
    if (session.end_time) {
      endTime = sessionsService.formatSessionTime(session.end_time, circuitTimeZone);
    }
    
    const timeDisplay = sessionDate ? `${sessionDate} ${startTime} - ${endTime}` : `${startTime} - ${endTime}`;
    return `${session.season_year} → ${formattedDate} → ${session.session_name} (${timeDisplay})`;
  };

  const calculateDelta = (leftVal: number | null | undefined, rightVal: number | null | undefined) => {
    if (leftVal == null || rightVal == null) return null;
    return leftVal - rightVal;
  };

  const formatValue = (value: number | string | null, unit: string, formatter?: (val: number) => string, isLoading?: boolean) => {
    if (isLoading) {
      return <Spin size="small" />;
    }
    if (value == null) {
      return '--';
    }
    // If it's a string (like wind direction), just display it with unit
    if (typeof value === 'string') {
      return `${value} ${unit}`;
    }
    const formatted = formatter ? formatter(value) : value.toFixed(1);
    return `${formatted} ${unit}`;
  };

  const renderDeltaCell = (delta: number | null, unit: string, formatter?: (val: number) => string) => {
    if (delta === null) {
      return <span className="delta-value neutral">/</span>;
    }

    const formattedDelta = formatter ? formatter(Math.abs(delta)) : Math.abs(delta).toFixed(1);
    const isNegative = delta < 0;
    const isZero = Math.abs(delta) < 0.01;

    if (isZero) {
      return <span className="delta-value neutral">same</span>;
    }

    return (
      <span className={`delta-value ${isNegative ? 'negative' : 'positive'}`}>
        {isNegative ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
        {formattedDelta} {unit}
      </span>
    );
  };

  // Helper to convert value from m/s to km/h if needed
  const convertValue = (value: number | null, convertToKmh?: boolean): number | null => {
    if (value === null) return null;
    return convertToKmh ? value * MS_TO_KMH : value;
  };

  // Prepare table data using new metrics array structure
  const prepareTableData = (): TableDataRow[] => {
    const data: TableDataRow[] = [];

    metricsWithUnits.forEach((config) => {
      // Special handling for wind direction
      if (config.metricName.toLowerCase().includes('direction')) {
        const leftDir = leftWeatherData?.main_wind_direction;
        const rightDir = rightWeatherData?.main_wind_direction;
        
        data.push({
          key: config.key,
          config,
          metricName: config.label,
          leftValue: leftDir ? `${getWindDirectionArrow(leftDir.direction)} ${leftDir.direction.toUpperCase()} (${leftDir.frequency.toFixed(1)}%)` : null,
          rightValue: rightDir ? `${getWindDirectionArrow(rightDir.direction)} ${rightDir.direction.toUpperCase()} (${rightDir.frequency.toFixed(1)}%)` : null,
          delta: null, // No delta for wind direction
          unit: '',
          formatter: config.formatter,
        });
        return;
      }

      // Main row (average) - use getMetricValue helper and apply conversion if needed
      const rawLeftAvg = leftWeatherData ? getMetricValue(leftWeatherData, config.metricName, 'avg') : null;
      const rawRightAvg = rightWeatherData ? getMetricValue(rightWeatherData, config.metricName, 'avg') : null;
      const leftAvg = convertValue(rawLeftAvg, config.convertToKmh);
      const rightAvg = convertValue(rawRightAvg, config.convertToKmh);
      const delta = calculateDelta(leftAvg, rightAvg);

      data.push({
        key: config.key,
        config,
        metricName: `${config.label} avg`,
        leftValue: leftAvg ?? null,
        rightValue: rightAvg ?? null,
        delta,
        unit: config.unit,
        formatter: config.formatter,
      });
    });

    return data;
  };

  const tableData = prepareTableData();

  const availableStations = effectiveStations;
  const leftSession = sessions.find((s) => {
    const sessionKey = `${s.season_year}|${s.circuit_id}|${s.start_date}|${s.session_name}`;
    return sessionKey === leftSessionId;
  });
  const rightSession = sessions.find((s) => {
    const sessionKey = `${s.season_year}|${s.circuit_id}|${s.start_date}|${s.session_name}`;
    return sessionKey === rightSessionId;
  });

  // Define table columns
  const columns: ColumnsType<TableDataRow> = [
    {
      title: 'Metric',
      key: 'metric',
      width: '35%',
      render: (_, record) => (
        <div className="metric-cell">
          <span className="metric-icon">{getFieldIcon(record.config.metricName, { size: 20 })}</span>
          <span className="metric-name">{record.metricName}</span>
          {record.unit && <span className="metric-unit">({record.unit})</span>}
        </div>
      ),
    },
    {
      title: 'Δ',
      key: 'delta',
      width: '20%',
      align: 'center',
      render: (_, record) => renderDeltaCell(record.delta, record.unit, record.formatter),
    },
    {
      title: leftSession ? formatSessionOption(leftSession) : 'Left',
      key: 'left',
      width: '22.5%',
      align: 'center',
      render: (_, record) => formatValue(record.leftValue, record.unit, record.formatter, loadingLeftWeather),
    },
    {
      title: rightSession ? formatSessionOption(rightSession) : 'Right',
      key: 'right',
      width: '22.5%',
      align: 'center',
      render: (_, record) => formatValue(record.rightValue, record.unit, record.formatter, loadingRightWeather),
    },
  ];

  // Expandable row configuration - use new metrics structure
  const expandable = {
    expandedRowKeys,
    onExpand: (_expanded: boolean, record: TableDataRow) => {
      toggleRow(record.key);
    },
    expandedRowRender: (record: TableDataRow) => {
      if (!record.config.hasMinMax) return null;

      // Get raw values and apply conversion if needed (e.g., m/s to km/h for wind)
      const rawLeftMin = leftWeatherData ? getMetricValue(leftWeatherData, record.config.metricName, 'min') : null;
      const rawRightMin = rightWeatherData ? getMetricValue(rightWeatherData, record.config.metricName, 'min') : null;
      const rawLeftMax = leftWeatherData ? getMetricValue(leftWeatherData, record.config.metricName, 'max') : null;
      const rawRightMax = rightWeatherData ? getMetricValue(rightWeatherData, record.config.metricName, 'max') : null;
      
      const leftMin = convertValue(rawLeftMin, record.config.convertToKmh);
      const rightMin = convertValue(rawRightMin, record.config.convertToKmh);
      const leftMax = convertValue(rawLeftMax, record.config.convertToKmh);
      const rightMax = convertValue(rawRightMax, record.config.convertToKmh);

      return (
        <div className="expanded-details">
          <div className="detail-row">
            <span className="detail-label">{record.config.label} min:</span>
            <span className="detail-value">{renderDeltaCell(calculateDelta(leftMin, rightMin), record.unit, record.formatter)}</span>
            <span className="detail-value">{formatValue(leftMin, record.unit, record.formatter)}</span>
            <span className="detail-value">{formatValue(rightMin, record.unit, record.formatter)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{record.config.label} max:</span>
            <span className="detail-value">{renderDeltaCell(calculateDelta(leftMax, rightMax), record.unit, record.formatter)}</span>
            <span className="detail-value">{formatValue(leftMax, record.unit, record.formatter)}</span>
            <span className="detail-value">{formatValue(rightMax, record.unit, record.formatter)}</span>
          </div>
        </div>
      );
    },
    rowExpandable: (record: TableDataRow) => record.config.hasMinMax,
  };

  if (!circuitId) {
    return (
      <Alert
        title="No circuit"
        description="Select an event or a circuit to compare sessions."
        type="info"
        showIcon
      />
    );
  }

  return (
    <div className="session-comparison">
      <Text className="comparison-title">Session Comparison</Text>
      
      {loadingSessions ? (
        <div className="loading-container">
          <Spin />
          <span>Loading sessions...</span>
        </div>
      ) : (
        <>
          <div className="comparison-selectors">
            <div className="selector-group">
              <Select
                className="session-select"
                placeholder="Select left session"
                value={leftSessionId}
                onChange={(value) => setLeftSessionId(value)}
                options={sessions.map((session) => ({
                  value: `${session.season_year}|${session.circuit_id}|${session.start_date}|${session.session_name}`,
                  label: formatSessionOption(session),
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label?.toString().toLowerCase() || '').includes(input.toLowerCase())
                }
              />
              <Select
                className="station-select"
                value={leftStation}
                onChange={(value) => setLeftStation(value)}
                options={[
                  { value: 'all', label: 'All Stations' },
                  ...availableStations.map((station) => ({
                    value: station.reference,
                    label: `Station ${station.reference}`,
                  })),
                ]}
              />
            </div>
            
            <Button
              className="swap-button"
              onClick={() => {
                const tempSession = leftSessionId;
                const tempStation = leftStation;
                setLeftSessionId(rightSessionId);
                setLeftStation(rightStation);
                setRightSessionId(tempSession);
                setRightStation(tempStation);
              }}
            >
              ⇄
            </Button>
            
            <div className="selector-group">
              <Select
                className="session-select"
                placeholder="Select right session"
                value={rightSessionId}
                onChange={(value) => setRightSessionId(value)}
                options={sessions.map((session) => ({
                  value: `${session.season_year}|${session.circuit_id}|${session.start_date}|${session.session_name}`,
                  label: formatSessionOption(session),
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label?.toString().toLowerCase() || '').includes(input.toLowerCase())
                }
              />
              <Select
                className="station-select"
                value={rightStation}
                onChange={(value) => setRightStation(value)}
                options={[
                  { value: 'all', label: 'All Stations' },
                  ...availableStations.map((station) => ({
                    value: station.reference,
                    label: `Station ${station.reference}`,
                  })),
                ]}
              />
            </div>
          </div>

          {(leftWeatherData === null && !loadingLeftWeather && leftSessionId) || 
           (rightWeatherData === null && !loadingRightWeather && rightSessionId) ? (
            <Alert
              title="Missing Data"
              description="Weather data is not available for one or both selected sessions."
              type="warning"
              showIcon
              className="data-warning"
            />
          ) : null}

          <div className="comparison-table-container">
            <Table
              className="comparison-table"
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="middle"
              expandable={expandable}
              rowKey="key"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SessionComparison;
